import { ORDER_TYPE } from '@exam-management/common/utils';
import {
  FilterOperatorEnum,
  type QBankStructureAuditLogOrderType,
  useQbsSearchQBankStructureAuditLogsQuery,
} from '@exam-management/graphql';
import { useParams } from '@exam-management/router';
import { DataGrid, type GridSortModel, InfoOutlinedIcon, NoRowsOverlay } from '@shared/components';

import { useQBankStructureActivityLogSearchParams } from '../../hooks';

import { columns } from './column';

export default function QBankStructureActivityLogList() {
  const { structureId } = useParams('/qbank-structures/:structureId/activity-log');
  const [{ page, pageSize, sort, field }, setQBSActivityLogSearchParams] =
    useQBankStructureActivityLogSearchParams();

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? 'actionTime';
    const sort = sorting[0]?.sort ?? 'desc';
    setQBSActivityLogSearchParams({ sort: sort, field: field });
  };

  const { data, loading } = useQbsSearchQBankStructureAuditLogsQuery({
    variables: {
      input: {
        order: {
          field,
          type: ORDER_TYPE[sort] as unknown as QBankStructureAuditLogOrderType,
        },
        filter: {
          conditions: [
            {
              field: 'executionResult',
              operator: FilterOperatorEnum.Equal,
              value: 'SUCCESS',
            },
          ],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        qBankStructureId: structureId,
      },
    },
    fetchPolicy: 'network-only',
  });

  const total = data?.qbsSearchQBankStructureAuditLogs.total ?? 0;
  const isSearchResultEmpty = total === 0;

  return (
    <DataGrid
      rows={data?.qbsSearchQBankStructureAuditLogs.qBankStructureAuditLogs || []}
      columns={columns}
      getRowHeight={() => 'auto'}
      columnHeaderHeight={48}
      disableColumnMenu
      sortingMode='server'
      onSortModelChange={handleSortModelChange}
      loading={loading}
      slots={{
        noRowsOverlay: () =>
          !isSearchResultEmpty ? (
            <NoRowsOverlay icon={InfoOutlinedIcon} title='ยังไม่มีข้อมูล Activity Logs' />
          ) : (
            <NoRowsOverlay />
          ),
      }}
      slotProps={{
        footer: {
          total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_, newPage: number) => {
              setQBSActivityLogSearchParams({ page: newPage });
            },
            onRowsPerPageChange: (event) => {
              const newPageSize = parseInt(event.target.value, 10);
              setQBSActivityLogSearchParams({ page: 0, pageSize: newPageSize });
            },
          },
        },
      }}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(2),
        border: `1px solid ${theme.palette.grey[300]}`,
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiDataGrid-row .MuiDataGrid-cell': {
          padding: theme.spacing(3),
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          ...theme.typography.body2,
        },
        '& .MuiDataGrid-columnHeader': {
          px: 3,
        },
        '--DataGrid-overlayHeight': '400px',
      })}
    />
  );
}
